import React, { useEffect, useState } from 'react';
import { DragDropContext } from '@hello-pangea/dnd';
import KanbanColumn from './KanbanColumn';
import CompleteWorkOrderModal from './CompleteWorkOrderModal';
import EstimateGateModal from './EstimateGateModal';
import { useUpdateWorkOrderStatus } from '../../../custom-hooks/use-maintenance';
import { MAINTENANCE_STATUSES } from '../../../utils/enums';
import { STATUS_COLUMNS, STATUS_META } from '../utils';

const groupByColumn = (workOrders) => {
    const columns = {};
    STATUS_COLUMNS.forEach((status) => { columns[status] = []; });
    workOrders.forEach((workOrder) => {
        if (columns[workOrder.status]) columns[workOrder.status].push(workOrder);
    });
    STATUS_COLUMNS.forEach((status) => columns[status].sort((a, b) => a.position - b.position));
    return columns;
};

const MaintenanceBoard = ({ workOrders, onCardClick, onQuickView, canViewCosts, canMove }) => {
    // Mirrors server data locally so a drop can move instantly, before the
    // status/position mutation round-trips — reconciled whenever fresh data arrives.
    const [localWorkOrders, setLocalWorkOrders] = useState(workOrders);
    useEffect(() => { setLocalWorkOrders(workOrders); }, [workOrders]);

    const updateStatusMutation = useUpdateWorkOrderStatus();
    // A drop into Completed without an actual cost pauses here instead of moving —
    // the card visually snaps back (we never touch state) until the modal is
    // confirmed or cancelled.
    const [pendingComplete, setPendingComplete] = useState(null);
    // Same pause, for a card dragged out of To Do with no estimate on it.
    const [pendingEstimate, setPendingEstimate] = useState(null);

    const columns = groupByColumn(localWorkOrders);

    const applyMove = (draggableId, destinationStatus, before, after, actualCost, estimate) => {
        let position;
        if (before && after) position = (before.position + after.position) / 2;
        else if (before) position = before.position + 1;
        else if (after) position = after.position - 1;
        else position = 0;

        setLocalWorkOrders((prev) => prev.map((w) => (
            w._id === draggableId ? { ...w, status: destinationStatus, position } : w
        )));

        updateStatusMutation.mutate({
            workOrderId: draggableId,
            data: { status: destinationStatus, beforeId: before?._id, afterId: after?._id, actualCost, estimate },
        });
    };

    const onDragEnd = (result) => {
        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (destination.droppableId === source.droppableId && destination.index === source.index) return;
        if (!canMove) return;

        const destColumn = columns[destination.droppableId].filter((w) => w._id !== draggableId);
        const before = destColumn[destination.index - 1];
        const after = destColumn[destination.index];

        const draggedWorkOrder = localWorkOrders.find((w) => w._id === draggableId);

        // Leaving To Do needs an estimate. Checked before the Completed gate so a card
        // with neither is asked for the estimate first rather than the actual cost.
        if (draggedWorkOrder?.status === MAINTENANCE_STATUSES.TODO
            && destination.droppableId !== MAINTENANCE_STATUSES.TODO
            && draggedWorkOrder?.estimate == null) {
            setPendingEstimate({ workOrder: draggedWorkOrder, before, after, destinationStatus: destination.droppableId });
            return;
        }

        if (destination.droppableId === MAINTENANCE_STATUSES.COMPLETED && draggedWorkOrder?.actualCost == null) {
            setPendingComplete({ workOrder: draggedWorkOrder, before, after });
            return;
        }

        applyMove(draggableId, destination.droppableId, before, after);
    };

    const handleConfirmEstimate = (estimate) => {
        if (!pendingEstimate) return;
        const { workOrder, before, after, destinationStatus } = pendingEstimate;
        applyMove(workOrder._id, destinationStatus, before, after, undefined, estimate);
        setPendingEstimate(null);
    };

    const handleConfirmComplete = (actualCost) => {
        if (!pendingComplete) return;
        const { workOrder, before, after } = pendingComplete;
        applyMove(workOrder._id, MAINTENANCE_STATUSES.COMPLETED, before, after, actualCost);
        setPendingComplete(null);
    };

    return (
        <div className="h-full w-full overflow-x-auto pb-4 custom-scrollbar">
            <DragDropContext onDragEnd={onDragEnd}>
                <div className="flex h-full min-w-max gap-6">
                    {STATUS_COLUMNS.map((status) => (
                        <KanbanColumn
                            key={status}
                            status={status}
                            title={STATUS_META[status]?.label || status}
                            workOrders={columns[status]}
                            onCardClick={onCardClick}
                            onQuickView={onQuickView}
                            canViewCosts={canViewCosts}
                            canMove={canMove}
                        />
                    ))}
                </div>
            </DragDropContext>

            <EstimateGateModal
                isOpen={Boolean(pendingEstimate)}
                onClose={() => setPendingEstimate(null)}
                workOrder={pendingEstimate?.workOrder}
                targetStatus={pendingEstimate?.destinationStatus}
                onConfirm={handleConfirmEstimate}
                isSubmitting={updateStatusMutation.isLoading}
            />

            <CompleteWorkOrderModal
                isOpen={!!pendingComplete}
                onClose={() => setPendingComplete(null)}
                workOrder={pendingComplete?.workOrder}
                onConfirm={handleConfirmComplete}
                isSubmitting={updateStatusMutation.isLoading}
            />
        </div>
    );
};

export default MaintenanceBoard;
